'use strict';

import { SurveyListing } from '../../../services/buyer-api/survey.interface';
import {
  EachCountry,
  EachLanguage,
} from '../../../interfaces/common-service.interface';
import { CountryCodeMapper, SurveyClassifier } from '@purespectrum1/ui';

export interface LocaleData {
  survey_localization: string;
  country_name: string;
  country_code: string;
  language_name: string;
  language_code: string;
  language_short_code: string;
}

export class LocaleDataMapper {
  constructor(
    private readonly _survey: SurveyListing,
    private readonly _countries: EachCountry[]
  ) {}

  public map(): LocaleData[] {
    if (!this._countries.length) {
      return [];
    }
    return this._locales().map((locale) => this._mapEach(locale));
  }

  private _mapEach(locale: string): LocaleData {
    const [languageShortCode, countryCode] = locale.split('_');
    const matchedCountry = this._matchedCountry(
      CountryCodeMapper.forUI(countryCode).map()
    );
    const matchedLanguage = this._matchedLanguage(
      matchedCountry,
      languageShortCode
    );

    return {
      survey_localization: locale,
      ...this._mappedCountryData(matchedCountry),
      ...this._mappedLanguageData(matchedLanguage),
    };
  }

  private _matchedCountry(code: string): EachCountry {
    return this._countries.find((country) => country.short_Code === code)!;
  }

  private _matchedLanguage(
    matchedCountry: EachCountry,
    code: string
  ): EachLanguage {
    return matchedCountry.lang.find((l) => l.transalte_code === code)!;
  }

  private _mappedCountryData(
    country: EachCountry
  ): Pick<LocaleData, 'country_code' | 'country_name'> {
    const { name: country_name, short_Code: country_code } = country;
    return {
      country_code,
      country_name,
    };
  }

  private _mappedLanguageData(
    language: EachLanguage
  ): Pick<
    LocaleData,
    'language_code' | 'language_name' | 'language_short_code'
  > {
    const {
      name: language_name,
      short_code: language_code,
      transalte_code: language_short_code,
    } = language;
    return {
      language_name,
      language_code,
      language_short_code,
    };
  }

  private _locales(): string[] {
    if (SurveyClassifier.fromResponse(this._survey).projectParent()) {
      return [...new Set(this._survey.survey_localizations!)];
    }

    return this._survey.survey_localization
      ? [this._survey.survey_localization]
      : [];
  }
}
